/* ============================================================
 * src/screens/content/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-content.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. The MarkerEditor, Analyse,
// EditorialInbox, and AiSuggestions surfaces in this bundle bind to the
// real assets query where one exists, and to honest skeleton/empty
// states where no public surface is landed. No fixture dependency
// remains in this file.
import { fmtDur, fmtClock, ago, statusLabels } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { AssetPlayer, PosterGenerator } from '../pending/_player';
import { ReplaceFileModal, TrashModal } from '../pending/_bundle';
import { AudioVersionTab } from './_audio';
// T328 (FR-005) — FeedSources is the only export in this bundle that needs
// live Convex bindings. Creation is Payload-owned (see PRD §FR-005); the
// "Añadir fuente" affordance link-outs to Payload's admin form rather than
// invoking a Convex mutation that would inject a row outside the sync
// contract (`convex/sync/ingestSourcesReceiver.ts`).
import { useQuery, useAction } from 'convex/react';
import { api } from '@convex/api';
// The primitives-barrel `useMutationWithToast` (imported above) is the legacy
// in-memory `(fn, opts)` variant; the REAL Convex hook returns `{ run, loading }`.
import { useMutationWithToast as useConvexMutationWithToast } from '../../hooks/useMutationWithToast';
import { env } from '../../lib/env';
// T332 (FR-010) — content library wiring. The adapter layer maps backend
// literals onto the operator-facing display vocabulary the screens render.
import {
  ASSET_TYPE_LABELS,
  adaptAssetType,
  adaptTranscodingStatus,
  type AssetType,
} from '../../lib/adapters';

/* ============================================================
   T332 (FR-010) — Backend → screen row adapter for the public
   `api.assets.list` query. The prototype rendered a mock-shaped
   row (`id`, `type`, `status`, `markers`, `progress`, `addedAt`,
   `resolution` as a `'WIDTH×HEIGHT'` string); the live query
   surfaces the canonical backend shape (`_id`, `assetType`,
   `transcodingStatus`, `resolution: { width, height }`, etc.).
   This local function normalises one to the other so the existing
   JSX renders without invented fields (markers/progress are
   honestly absent — CLAUDE.md anti-pattern: "do not fabricate
   dashboard fields").
   ============================================================ */
interface AssetListRow {
  _id: string;
  title: string;
  assetType: AssetType;
  transcodingStatus: string;
  durationMs?: number;
  resolution?: { width: number; height: number };
  tags?: string[];
  thumbnailUrl?: string;
  playbackUrl?: string;
  createdAt?: number;
}
interface PrototypeAssetShape {
  id: string;
  title: string;
  /** Display label (Spanish) — `adaptAssetType` output. */
  type: string;
  /** Backend literal — used as the stable filter-button value. */
  assetType: AssetType;
  /** Mock-prototype 4-state vocabulary used by `assetStatusTone` /
   *  `statusLabels`: 'ready' | 'processing' | 'uploading' | 'failed'. */
  status: 'ready' | 'processing' | 'uploading' | 'failed';
  durationMs: number;
  resolution: string;
  tags: string[];
  thumbnailUrl?: string;
  playbackUrl?: string;
  addedAt: number;
}

/**
 * Collapse the backend `transcodingStatus` (9 literals) to the
 * 4-string vocabulary the existing screen JSX understands
 * ('ready' / 'processing' / 'uploading' / 'failed'). `uploading`
 * round-trips by name; everything else routes through the
 * `adaptTranscodingStatus` 4-bucket adapter.
 */
function uiAssetStatus(backend: string): 'ready' | 'processing' | 'uploading' | 'failed' {
  if (backend === 'uploading') return 'uploading';
  const bucket = adaptTranscodingStatus(backend);
  if (bucket === 'completed') return 'ready';
  if (bucket === 'failure') return 'failed';
  // pending + in_progress both fall through to the "processing" UI affordance.
  return 'processing';
}

function adaptAssetRow(row: AssetListRow): PrototypeAssetShape {
  return {
    id: row._id,
    title: row.title,
    assetType: row.assetType,
    type: adaptAssetType(row.assetType),
    status: uiAssetStatus(row.transcodingStatus),
    durationMs: row.durationMs ?? 0,
    resolution: row.resolution
      ? `${row.resolution.width}×${row.resolution.height}`
      : '—',
    tags: row.tags ?? [],
    thumbnailUrl: row.thumbnailUrl,
    playbackUrl: row.playbackUrl,
    addedAt: row.createdAt ?? 0,
  };
}

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Content Library arc
   ============================================================ */

function assetStatusTone(s) { return s === 'ready' ? 'good' : s === 'processing' ? 'warn' : s === 'uploading' ? 'info' : 'neutral'; }

/**
 * T332 (FR-010, WIRE-4) — `/library` wired to `api.assets.list`.
 *
 * Honest-state pattern (per CLAUDE.md §3): `useQuery` returns `undefined`
 * during the in-flight subscription, an empty array when the table is
 * empty, and the row list when data lands. Each branch renders a
 * distinct affordance — no mock fallback, no fabricated counts.
 *
 * Filter design: the four type-bucket buttons store backend literals
 * (`vod` / `filler` / `house_ad` / `bumper`); display labels come from
 * `ASSET_TYPE_LABELS` so the operator-facing UI stays Spanish. The
 * 5th option is the sentinel `'all'` (no filter). The Select input
 * uses the same literal values for parity.
 */
const LIBRARY_TYPE_FILTERS: Array<'all' | AssetType> = [
  'all', 'vod', 'filler', 'house_ad', 'bumper',
];
const LIBRARY_TYPE_LABELS: Record<'all' | AssetType, string> = {
  all: 'Todos',
  ...ASSET_TYPE_LABELS,
};

export function Library() {
  const [q, setQ] = useState('');
  const [type, setType] = useState<'all' | AssetType>('all');
  const [view, setView] = useState('grid');

  const raw = useQuery(api.assets.list, {}) as AssetListRow[] | undefined;

  // Memoised projection so the filter/search keystrokes don't re-walk
  // the row shape adapter on every keystroke.
  const rows = useMemo(
    () => (raw === undefined ? undefined : raw.map(adaptAssetRow)),
    [raw],
  );

  const filtered = useMemo(() => {
    if (rows === undefined) return undefined;
    const needle = q.trim().toLowerCase();
    return rows.filter((a) =>
      (!needle || a.title.toLowerCase().includes(needle)) &&
      (type === 'all' || a.assetType === type),
    );
  }, [rows, q, type]);

  const header = (
    <PageHead title="Contenido" description="Cada video en tu espacio de trabajo."
      actions={<><Dropdown trigger={<Btn variant="ghost" icon="plus" iconRight="chevronD">Importar</Btn>} items={[
        { label: 'Desde URL', icon: 'linkIcon' }, { label: 'Carga masiva', icon: 'uploadCloud' }, { label: 'Desde una fuente', icon: 'database', onClick: () => navigate('/feed-sources') }]} />
        <AccessGate perm="asset_create"><Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn></AccessGate></>} />
  );

  // ---- (1) LOADING ---------------------------------------------------------
  if (rows === undefined || filtered === undefined) {
    return (
      <div>
        {header}
        <SkeletonStatRow />
        <div style={{ height: 52 }} />
        <SkeletonCardGrid n={8} cols="circa-grid-4" media={120} />
      </div>
    );
  }

  // ---- (2) EMPTY -----------------------------------------------------------
  if (rows.length === 0) {
    return (
      <div>
        {header}
        <Panel>
          <EmptyState
            icon="film"
            title="Sin contenido todavía"
            body="Sube tu primer video o conecta una fuente para llenar la biblioteca."
            action={
              <AccessGate perm="asset_create">
                <Btn variant="solid" icon="upload" onClick={() => navigate('/library/upload')}>Subir contenido</Btn>
              </AccessGate>
            }
          />
        </Panel>
      </div>
    );
  }

  // ---- (3) DATA ------------------------------------------------------------
  const readyCount = rows.filter((a) => a.status === 'ready').length;
  const inFlightCount = rows.filter((a) => a.status === 'processing' || a.status === 'uploading').length;
  const totalDurationMs = rows.reduce((s, a) => s + a.durationMs, 0);

  return (
    <div>
      {header}
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Videos" value={rows.length} hint="en la biblioteca" icon="film" />
        <StatCard label="Listos" value={readyCount} tone="good" icon="checkCircle" />
        <StatCard label="En proceso" value={inFlightCount} tone="warn" icon="loader" />
        <StatCard label="Duración total" value={fmtDur(totalDurationMs)} icon="clock" />
      </div>
      <Panel pad={16} style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ width: 300 }}><Input value={q} onChange={setQ} icon="search" placeholder="Buscar por título" /></div>
          <div style={{ width: 200 }}>
            <Select
              value={type}
              onChange={(v) => setType(v as 'all' | AssetType)}
              full
              options={LIBRARY_TYPE_FILTERS.map((v) => ({ value: v, label: LIBRARY_TYPE_LABELS[v] }))}
            />
          </div>
          <div style={{ flex: 1 }} />
          <SegmentedControl value={view} onChange={setView} options={[{ value: 'grid', icon: 'grid' }, { value: 'list', icon: 'list' }]} />
        </div>
      </Panel>
      <div style={{ marginBottom: 14, display: 'none' }}><Pill tone="neutral">{filtered.length} videos</Pill></div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {LIBRARY_TYPE_FILTERS.map((tp) => {
          const n = tp === 'all' ? rows.length : rows.filter((a) => a.assetType === tp).length;
          return <button key={tp} onClick={() => setType(tp)} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontFamily: 'var(--font-sans)', display: 'inline-flex', alignItems: 'center', gap: 7,
            background: type === tp ? 'var(--ink-muted)' : 'transparent', color: type === tp ? '#fff' : 'var(--slate)', borderColor: type === tp ? 'var(--ink-muted)' : 'var(--border-cool)' }}>{LIBRARY_TYPE_LABELS[tp]}<span style={{ fontSize: 11, opacity: 0.7 }}>{n}</span></button>;
        })}
      </div>
      {view === 'grid' ? (
        <div className="circa-grid-4">
          {filtered.map((a) => (
            <div key={a.id} onClick={() => navigate('/library/' + a.id)} className="circa-lift" style={{ cursor: 'pointer', border: '1px solid var(--border-cool)', borderRadius: 18, overflow: 'hidden', background: 'var(--canvas)' }}>
              <div style={{ position: 'relative' }}>
                <Thumb radius={0} label={a.type} />
                <span style={{ position: 'absolute', top: 10, left: 10 }}><Pill tone="neutral" size="sm" style={{ background: 'rgba(255,255,255,0.92)' }}>{a.type}</Pill></span>
                <span style={{ position: 'absolute', top: 10, right: 10 }}><Pill tone={assetStatusTone(a.status)} size="sm">{statusLabels[a.status]}</Pill></span>
                <span className="t-mono" style={{ position: 'absolute', bottom: 10, right: 10, fontSize: 10, background: 'rgba(0,0,0,0.7)', color: '#fff', padding: '2px 6px', borderRadius: 4 }}>{fmtDur(a.durationMs)}</span>
                {/* T332: progress bar dropped — `api.assets.list` does not project
                    `transcodingProgress` (per the row-shape contract), and
                    fabricating a value here is the CLAUDE.md §12a anti-pattern. */}
              </div>
              <div style={{ padding: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.3, marginBottom: 8, height: 36, overflow: 'hidden' }}>{a.title}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>{a.resolution}</span>
                  {/* T332: marker badge dropped — `api.assets.list` does not project
                      `adMarkers.length`; the badge returns when a marker count
                      is added to the projection in a future milestone. */}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Panel pad={0}>
          {filtered.map((a, i) => (
            <div key={a.id} onClick={() => navigate('/library/' + a.id)} style={{ display: 'flex', gap: 16, alignItems: 'center', padding: '12px 20px', cursor: 'pointer', borderBottom: i < filtered.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--snow)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div style={{ width: 72, flexShrink: 0 }}><Thumb radius={6} /></div>
              <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 500, fontSize: 14 }}>{a.title}</div><div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{a.resolution} · {fmtDur(a.durationMs)}</div></div>
              <Pill tone="neutral" size="sm">{a.type}</Pill>
              <Pill tone={assetStatusTone(a.status)} size="sm">{statusLabels[a.status]}</Pill>
            </div>
          ))}
        </Panel>
      )}
    </div>
  );
}

/* Upload — ports the v2 S3 + AWS Batch pipeline verbatim
 * (frontend-v2/src/components/library/UploadAssetPage.tsx). Three HTTP
 * round-trips against the Convex `.convex.site` host:
 *
 *   1. POST /api/upload/url        — creates the asset row in `uploading`
 *                                    and mints an S3 presigned PUT URL.
 *   2. PUT  <s3-presigned-url>     — file bytes go directly to S3
 *                                    (Content-Type must match step 1).
 *   3. POST /api/upload/complete   — flips the asset to `pending`, kicks
 *                                    the AWS Batch ingest workflow.
 *
 * NOT Cloudflare Stream — that's a wrong pipeline this repo has rejected
 * twice. Cloudflare Stream wires were stub code from a prototype lift. */
type UploadStatus = 'queued' | 'creating' | 'uploading' | 'completing' | 'done' | 'error';
type AssetType =
  | 'vod' | 'filler' | 'house_ad' | 'bumper' | 'slate' | 'promo'
  | 'movie' | 'shortFormVideo' | 'tvSpecial' | 'series';

interface UploadRow {
  id: string;
  file: File;
  status: UploadStatus;
  progress: number;
  errorMessage?: string;
  assetId?: string;
  cloudfrontUrl?: string;
}

const SUPPORTED_EXT = ['mp4', 'mov', 'mkv', 'avi', 'webm', 'mpeg', 'mpg'];
const MAX_BYTES = 10 * 1024 * 1024 * 1024; // 10 GiB — matches v2.
const ASSET_TYPE_OPTIONS: ReadonlyArray<readonly [AssetType, string]> = [
  ['vod', 'VOD'], ['movie', 'Película'], ['tvSpecial', 'Especial'],
  ['series', 'Serie'], ['shortFormVideo', 'Video corto'], ['promo', 'Promo'],
  ['bumper', 'Bumper'], ['house_ad', 'House ad'], ['filler', 'Filler'],
  ['slate', 'Slate'],
];
const STATUS_LABEL: Record<UploadStatus, string> = {
  queued: 'En cola', creating: 'Registrando', uploading: 'Subiendo',
  completing: 'Finalizando', done: 'Listo', error: 'Error',
};

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

function fileTitleStem(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[_-]+/g, ' ').trim();
}

function isSupported(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext !== undefined && SUPPORTED_EXT.includes(ext);
}

function putWithProgress(url: string, file: File, contentType: string, onProgress: (pct: number) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`S3 PUT falló (HTTP ${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Error de red durante la subida a S3.'));
    xhr.send(file);
  });
}

export function Upload() {
  const [drag, setDrag] = useState(false);
  const [queue, setQueue] = useState<UploadRow[]>([]);
  const [defaultAssetType, setDefaultAssetType] = useState<AssetType>('vod');
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // HTTP routes are mounted on the .convex.site host, not the WebSocket
  // .convex.cloud host — matches v2's UploadAssetPage URL swap.
  const httpBase = useMemo(
    () => env.CONVEX_URL.replace('.convex.cloud', '.convex.site'),
    [],
  );

  const patch = (id: string, p: Partial<UploadRow>) =>
    setQueue(prev => prev.map(r => r.id === id ? { ...r, ...p } : r));

  const uploadOne = useCallback(async (row: UploadRow, assetType: AssetType) => {
    try {
      patch(row.id, { status: 'creating' });
      const contentType = row.file.type || 'application/octet-stream';

      // Step 1 — request presigned URL + create asset row.
      const stepOne = await fetch(`${httpBase}/api/upload/url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename:    row.file.name,
          contentType,
          title:       fileTitleStem(row.file.name),
          assetType,
        }),
      });
      if (!stepOne.ok) {
        throw new Error(`Registro rechazado (HTTP ${stepOne.status}): ${await stepOne.text()}`);
      }
      const { assetId, uploadUrl, cloudfrontUrl } = (await stepOne.json()) as {
        assetId: string;
        uploadUrl: string;
        cloudfrontUrl: string;
        s3Key: string;
      };
      patch(row.id, { assetId, cloudfrontUrl, status: 'uploading' });

      // Step 2 — PUT bytes to S3 with progress.
      await putWithProgress(uploadUrl, row.file, contentType, (pct) => {
        patch(row.id, { progress: pct });
      });

      // Step 3 — signal completion → flips asset to `pending` + kicks
      // AWS Batch ingest workflow.
      patch(row.id, { status: 'completing' });
      const stepThree = await fetch(`${httpBase}/api/upload/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, cloudfrontUrl }),
      });
      if (!stepThree.ok) {
        throw new Error(`Finalización rechazada (HTTP ${stepThree.status}): ${await stepThree.text()}`);
      }

      patch(row.id, { status: 'done', progress: 100 });
    } catch (err) {
      patch(row.id, {
        status: 'error',
        errorMessage: err instanceof Error ? err.message : String(err),
      });
    }
  }, [httpBase]);

  const pickFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const accepted: UploadRow[] = [];
    const rejected: string[] = [];
    for (const file of Array.from(list)) {
      if (!isSupported(file)) { rejected.push(`${file.name}: formato no soportado`); continue; }
      if (file.size > MAX_BYTES) { rejected.push(`${file.name}: excede 10 GB`); continue; }
      accepted.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        file,
        status: 'queued',
        progress: 0,
      });
    }
    if (rejected.length > 0) {
      toast({ variant: 'error', message: rejected.join(' · ') });
    }
    setQueue(prev => [...prev, ...accepted]);
    for (const item of accepted) {
      void uploadOne(item, defaultAssetType);
    }
  };

  return (
    <div className="circa-cols-8-4">
      <div>
        <PageHead title="Subir contenido" description="Arrastra videos para añadirlos a tu biblioteca." />
        <div
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); pickFiles(e.dataTransfer.files); }}
          style={{ border: `2px dashed ${drag ? 'var(--cta)' : 'var(--border-cool)'}`, borderRadius: 22, padding: '56px 24px', textAlign: 'center', cursor: 'pointer', background: drag ? 'var(--pale-blue)' : 'var(--canvas)', transition: 'all .15s' }}
        >
          <Icon name="uploadCloud" size={48} color="var(--muted-slate)" />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 400, margin: '14px 0 6px' }}>{drag ? 'Suelta para subir' : 'Arrastra tus videos aquí'}</div>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--muted-slate)' }}>MP4, MOV, MKV, AVI, WebM o MPEG. Hasta 10 GB por archivo.</p>
          <Btn variant="ghost">Explorar archivos…</Btn>
          <input ref={fileInputRef} type="file" accept="video/*,.mkv,.mpeg,.mpg" multiple style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={e => { pickFiles(e.target.files); e.target.value = ''; }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 18 }}>
          <Field label="Tipo de contenido por defecto">
            <Select
              value={defaultAssetType}
              onChange={(v: string) => setDefaultAssetType(v as AssetType)}
              full
              options={ASSET_TYPE_OPTIONS.map(([value, label]) => ({ value, label }))}
            />
          </Field>
        </div>
        {queue.length > 0 && (
          <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {queue.map(r => {
              const isErr = r.status === 'error';
              const isDone = r.status === 'done';
              const showPct = r.status === 'uploading';
              const statusLabel = showPct ? `${r.progress}%` : STATUS_LABEL[r.status];
              const barColor = isErr ? 'var(--bad)' : isDone ? 'var(--good)' : 'var(--cta)';
              const barValue = isDone || r.status === 'completing' ? 100 : r.progress;
              return (
                <Panel key={r.id} pad={14}>
                  <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                    <div style={{ width: 80, flexShrink: 0 }}><Thumb radius={8} /></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                        <span style={{ fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.file.name}</span>
                        <span className="t-mono" style={{ fontSize: 11, color: isErr ? 'var(--bad)' : 'var(--muted-slate)' }}>{statusLabel}</span>
                      </div>
                      <Progress value={barValue} color={barColor} />
                      {r.errorMessage && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--bad)', wordBreak: 'break-word' }}>{r.errorMessage}</div>}
                      {isDone && r.assetId && (
                        <div style={{ marginTop: 6, fontSize: 12, color: 'var(--muted-slate)' }}>
                          Procesando en AWS Batch · <a style={{ color: 'var(--cta)', textDecoration: 'none' }} onClick={(e) => { e.preventDefault(); navigate('/library/' + r.assetId); }} href={`/library/${r.assetId}`}>Ver en biblioteca</a>
                        </div>
                      )}
                    </div>
                    <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{formatBytes(r.file.size)}</span>
                  </div>
                </Panel>
              );
            })}
          </div>
        )}
      </div>
      <Panel title="Importar desde URL">
        <Field label="URL del video"><Input placeholder="https://…" icon="linkIcon" /></Field>
        <Btn variant="ghost" full style={{ marginTop: 12 }} onClick={() => toast({ variant: 'info', message: 'Importación desde URL aún no disponible — usa el cargador de archivos por ahora.' })}>Importar</Btn>
        <div style={{ height: 1, background: 'var(--lightest-gray)', margin: '20px 0' }} />
        <p style={{ fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.5 }}>Los archivos van directo a S3; luego AWS Batch transcodifica y publica el resultado en la biblioteca.</p>
      </Panel>
    </div>
  );
}

/* ContentDetail — wired to api.assets.list (no asset.get exists yet, so we
 * filter the list by id). Honest-state contract: the Procesamiento panel
 * reads `transcodingStatus` directly; tabs that need backend data the
 * schema does not project (transcript / scenes / usage) render empty
 * states with a "pendiente" reason instead of fabricating rows. */
type RealAsset = {
  _id: string;
  title: string;
  assetType?: string;
  transcodingStatus: 'validating' | 'queued' | 'dispatched' | 'processing' | 'uploading' | 'completed' | 'failed' | 'dead';
  durationMs?: number;
  createdAt?: number;
  resolution?: { width: number; height: number } | null;
  tags?: string[];
  thumbnailUrl?: string;
  playbackUrl?: string;
};

const TRANSCODE_TONE: Record<RealAsset['transcodingStatus'], 'good' | 'warn' | 'bad' | 'neutral'> = {
  validating:  'warn',
  queued:      'warn',
  dispatched:  'warn',
  processing:  'warn',
  uploading:   'warn',
  completed:   'good',
  failed:      'bad',
  dead:        'bad',
};
const TRANSCODE_LABEL: Record<RealAsset['transcodingStatus'], string> = {
  validating:  'Validando origen',
  queued:      'En cola',
  dispatched:  'En edge',
  processing:  'Transcodificando',
  uploading:   'Subiendo a S3',
  completed:   'Completo',
  failed:      'Falló',
  dead:        'Falló (sin reintentos)',
};

// Scene + keyframe gallery — renders the real analysis output
// (api.analysis.queries.getSceneSegments + getKeyframes). Shared by
// ContentDetail's "Escenas" tab and the Analyse screen. Honest states:
// undefined -> skeleton, [] -> empty (with a link to start analysis),
// rows -> a keyframe thumbnail per scene with its time range.
function SceneGallery({ assetId }: { assetId?: string }) {
  const scenes = useQuery(
    api.analysis.queries.getSceneSegments,
    assetId ? { assetId: assetId as any } : 'skip',
  ) as Array<{ index: number; startTimeNs: bigint; endTimeNs: bigint; durationMs: number }> | undefined;
  const keyframes = useQuery(
    api.analysis.queries.getKeyframes,
    assetId ? { assetId: assetId as any } : 'skip',
  ) as Array<{ sceneIndex: number; url?: string | null }> | undefined;

  if (scenes === undefined || keyframes === undefined) return <SkeletonCardGrid n={6} />;
  if (scenes.length === 0) {
    return (
      <EmptyState
        icon="film"
        title="Sin escenas detectadas"
        body="Inicia un análisis para detectar escenas, fotogramas clave y segmentos de audio."
        action={assetId ? <Btn variant="solid" icon="sparkles" onClick={() => navigate('/library/' + assetId + '/analyse')}>Analizar video</Btn> : undefined}
      />
    );
  }
  const kfByScene = new Map<number, { url?: string | null }>();
  for (const kf of keyframes) kfByScene.set(kf.sceneIndex, kf);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 14 }}>
      {scenes.map((s) => {
        const kf = kfByScene.get(s.index);
        const startMs = Number(s.startTimeNs / 1_000_000n);
        const endMs = Number(s.endTimeNs / 1_000_000n);
        return (
          <div key={s.index} style={{ border: '1px solid var(--lightest-gray)', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
            <div style={{ aspectRatio: '16 / 9', background: 'var(--lightest-gray)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {kf?.url
                ? <img src={kf.url} alt={`Escena ${s.index + 1}`} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : <Icon name="film" size={22} color="var(--muted-slate)" />}
            </div>
            <div style={{ padding: '8px 10px' }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Escena {s.index + 1}</div>
              <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 2 }}>
                {fmtClock(startMs)}–{fmtClock(endMs)} · {fmtDur(s.durationMs)}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function ContentDetail({ id }) {
  const { toast } = useToast();
  const [replace, setReplace] = useState(false);
  const [trash, setTrash] = useState(false);
  const [tab, setTab] = useState('overview');

  // No `assets.get` endpoint exists; the list query is bounded and
  // newest-first which keeps the .find lookup cheap. `undefined` =
  // loading; `null` (after resolved) = not-found.
  const allAssets = useQuery(api.assets.list, { limit: 200 }) as RealAsset[] | undefined;
  const a = allAssets === undefined ? undefined : (allAssets.find(x => x._id === id) ?? null);

  // Real analysis/enrichment state for the "Procesamiento" panel pills.
  const sceneRows = useQuery(
    api.analysis.queries.getSceneSegments,
    id ? { assetId: id as any } : 'skip',
  ) as Array<unknown> | undefined;
  const enrich = useQuery(
    api.analysis.queries.getEnrichmentStatus,
    id ? { assetId: id as any } : 'skip',
  ) as { analysisStatus?: string; enrichmentResultCount?: number; enrichmentJobId?: string | null } | undefined | null;

  if (a === undefined) {
    return (
      <div>
        <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }]} title="Cargando…" />
        <SkeletonPanel rows={6} />
      </div>
    );
  }
  if (a === null) {
    return (
      <div>
        <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }]} title="No encontrado" />
        <Panel><EmptyState icon="film" title="No encontramos este video" body="Puede haber sido movido a la papelera o nunca terminó de subir." action={<Btn variant="solid" onClick={() => navigate('/library')}>Volver a la biblioteca</Btn>} /></Panel>
      </div>
    );
  }

  const ready = a.transcodingStatus === 'completed';
  const inProgress = !ready && a.transcodingStatus !== 'failed' && a.transcodingStatus !== 'dead';
  const tags = a.tags ?? [];
  const resolutionLabel = a.resolution ? `${a.resolution.width}×${a.resolution.height}` : '—';
  const durationLabel = a.durationMs ? fmtDur(a.durationMs) : '—';

  // "Procesamiento" pills, derived from real backend state (no fabricated
  // "Completo"). Escenas reflects the scene-analysis pipeline; Transcripción /
  // Enriquecimiento reflect the heavy-plane enrichment pipeline.
  const analysisState = enrich?.analysisStatus ?? 'none';
  const enrichCount = enrich?.enrichmentResultCount ?? 0;
  const enrichBusy = !!enrich?.enrichmentJobId;
  const stagePill = (done: boolean, processing: boolean, failed: boolean) =>
    done ? { tone: 'good', text: 'Completado' }
      : failed ? { tone: 'bad', text: 'Falló' }
        : processing ? { tone: 'warn', text: 'Procesando' }
          : { tone: 'neutral', text: 'Pendiente' };
  const procRows: Array<{ label: string; pill: { tone: string; text: string } }> = [
    { label: 'Escenas', pill: stagePill((sceneRows?.length ?? 0) > 0, analysisState === 'processing' || analysisState === 'pending', analysisState === 'failed') },
    { label: 'Transcripción', pill: stagePill(enrichCount > 0, enrichBusy, false) },
    { label: 'Enriquecimiento', pill: stagePill(enrichCount > 0, enrichBusy, false) },
  ];

  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title }]} title={a.title}
        actions={<>
          <AccessGate perm="schedule_create"><Btn variant="ghost" icon="calendar" onClick={() => toast({ variant: 'info', message: 'Elige una programación para añadirlo.' })}>Añadir a programación</Btn></AccessGate>
          <Btn variant="ghost" icon="zap" onClick={() => navigate('/library/' + a._id + '/markers')}>Marcar cortes</Btn>
          <Btn variant="ghost" icon="sparkles" onClick={() => navigate('/library/' + a._id + '/analyse')}>Analizar video</Btn>
          <Dropdown trigger={<Btn variant="ghost" icon="moreH" />} items={[
            { label: 'Editar metadatos', icon: 'edit', onClick: () => navigate('/library/' + a._id + '/metadata') },
            { label: 'Administrar subtítulos', icon: 'fileText', onClick: () => navigate('/library/' + a._id + '/captions') },
            { label: 'Reemplazar archivo', icon: 'refresh', onClick: () => setReplace(true) },
            { divider: true }, { label: 'Mover a la papelera', icon: 'trash', danger: true, onClick: () => setTrash(true) }]} />
        </>} />
      {!ready && (
        <FreshnessBanner show message={inProgress
          ? `Este video se está procesando (${TRANSCODE_LABEL[a.transcodingStatus]}). Algunas acciones estarán disponibles al terminar.`
          : 'La conversión falló. Reemplaza el archivo o reintenta.'} />
      )}
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {ready && a.playbackUrl
            ? <AssetPlayer descriptor={{
                assetId: a._id,
                mediaKind: 'video',
                source: { url: a.playbackUrl, contentType: 'application/x-mpegURL' },
                title: a.title,
                posterUrl: a.thumbnailUrl ?? null,
                durationMs: a.durationMs ?? 0,
                textTracks: [],
              }} />
            : <Panel><EmptyState icon="film" title={ready ? 'Reproductor no disponible' : 'Aún no se puede reproducir'} body={ready ? 'El video terminó de procesarse pero no tiene URL de reproducción.' : 'Termina el procesamiento para previsualizar.'} /></Panel>}
          <div>
            <Tabs tabs={[{ id: 'overview', label: 'Resumen' }, { id: 'transcript', label: 'Transcripción' }, { id: 'scenes', label: 'Escenas' }, { id: 'audio', label: 'Audio' }, { id: 'usage', label: 'Uso' }]} active={tab} onChange={setTab} style={{ marginBottom: 20 }} />
            {tab === 'overview' && (
              <Panel title="Acerca de este video">
                {tags.length > 0
                  ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>{tags.map(t => <CircaTag key={t}>{t}</CircaTag>)}</div>
                  : <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--muted-slate)' }}>Sin etiquetas. Añádelas en "Editar metadatos".</p>}
                <div style={{ borderTop: '1px solid var(--lightest-gray)', paddingTop: 18 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Miniatura</div>
                  {a.thumbnailUrl
                    ? <img src={a.thumbnailUrl} alt="" style={{ maxWidth: '100%', borderRadius: 12 }} />
                    : <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>La miniatura aparecerá cuando termine la conversión.</div>}
                </div>
              </Panel>
            )}
            {tab === 'transcript' && (
              <Panel title="Transcripción">
                {/* Transcripts come from a separate analysis pipeline that is
                    not yet projected into `assets.list`. Until that lands,
                    render empty state honestly. */}
                <EmptyState icon="fileText" title="Sin transcripción todavía" body="La transcripción automática aún no está disponible en este entorno." />
              </Panel>
            )}
            {tab === 'scenes' && (
              <Panel title="Escenas detectadas">
                <SceneGallery assetId={a._id} />
              </Panel>
            )}
            {tab === 'audio' && <AudioVersionTab asset={a as any} />}
            {tab === 'usage' && (
              <Panel title="Programaciones que lo usan">
                <EmptyState icon="calendar" title="Sin programaciones" body="Aún no hay un servicio que liste las programaciones que referencian este contenido." />
              </Panel>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Detalles">
            {[
              ['Estado', TRANSCODE_LABEL[a.transcodingStatus]],
              ['Tipo', a.assetType ?? '—'],
              ['Duración', durationLabel],
              ['Resolución', resolutionLabel],
              ['Añadido', a.createdAt ? ago(a.createdAt) : '—'],
            ].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--lightest-gray)', fontSize: 14 }}>
                <span style={{ color: 'var(--muted-slate)' }}>{k}</span><span>{v}</span>
              </div>
            ))}
          </Panel>
          <Panel title="Procesamiento">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
              <span style={{ fontSize: 14 }}>Conversión</span>
              <Pill tone={TRANSCODE_TONE[a.transcodingStatus]} size="sm" dot={inProgress}>{TRANSCODE_LABEL[a.transcodingStatus]}</Pill>
            </div>
            {/* Derived from real backend state: getSceneSegments (Escenas) and
                getEnrichmentStatus (Transcripción / Enriquecimiento). */}
            {procRows.map(({ label, pill }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--lightest-gray)' }}>
                <span style={{ fontSize: 14 }}>{label}</span>
                <Pill tone={pill.tone as any} size="sm">{pill.text}</Pill>
              </div>
            ))}
          </Panel>
          <Panel title="Pistas de subtítulos" right={<Btn size="sm" variant="text" onClick={() => navigate('/library/' + a._id + '/captions')}>Administrar</Btn>}>
            <div style={{ fontSize: 13, color: 'var(--muted-slate)' }}>Los subtítulos se gestionan en una página dedicada.</div>
          </Panel>
        </div>
      </div>
      <ReplaceFileModal open={replace} onClose={() => setReplace(false)} />
      <TrashModal open={trash} onClose={() => setTrash(false)} count={1} />
    </div>
  );
}

// Wired to the real analysis pipeline: api.analysis.mutations.analyzeAsset
// kicks off a durable job; api.analysis.queries.getAnalysisJobStatus is the
// live subscription that drives the status pill + progress; the completed
// counts come from the real segment/keyframe queries. No fabricated stats,
// no setInterval theatre. (Forward-ported from frontend-v2 AnalyseVideoPage.)
const ANALYSE_STAGE_LABEL: Record<string, string> = {
  PENDING: 'En cola',
  DOWNLOADING: 'Descargando origen',
  DETECTING: 'Detectando escenas',
  EXTRACTING_AUDIO: 'Segmentando audio',
  EXTRACTING_KEYFRAMES: 'Extrayendo fotogramas',
  UPLOADING: 'Subiendo resultados',
  COMPLETED: 'Completado',
  FAILED: 'Falló',
};
export function Analyse({ id }: { id?: string }) {
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    id ? { assetId: id as any } : 'skip',
  ) as { _id?: string; title?: string } | undefined | null;
  const job = useQuery(
    api.analysis.queries.getAnalysisJobStatus,
    id ? { assetId: id as any } : 'skip',
  ) as { status?: string; state?: string; progress?: number; error?: string } | undefined | null;
  const scenes = useQuery(api.analysis.queries.getSceneSegments, id ? { assetId: id as any } : 'skip') as Array<unknown> | undefined;
  const audio = useQuery(api.analysis.queries.getAudioSegments, id ? { assetId: id as any } : 'skip') as Array<{ silent?: boolean }> | undefined;
  const keyframes = useQuery(api.analysis.queries.getKeyframes, id ? { assetId: id as any } : 'skip') as Array<unknown> | undefined;
  const { run: startAnalysis, loading: starting } = useConvexMutationWithToast(
    api.analysis.mutations.analyzeAsset,
    {
      logLabel: 'asset_analyze_start',
      successMessage: 'Análisis iniciado.',
      errorMessagePrefix: 'No se pudo iniciar el análisis',
    },
  );

  if (id && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (id && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  const a = aDoc ?? { _id: '', title: '—' };

  // `null` job = never analysed; `undefined` = loading the job subscription.
  const jobStatus = job?.status ?? (job === null ? 'idle' : 'loading');
  const processing = jobStatus === 'processing' || jobStatus === 'pending';
  const completed = jobStatus === 'completed';
  const failed = jobStatus === 'failed';
  const pillTone = completed ? 'good' : failed ? 'bad' : processing ? 'warn' : 'neutral';
  const pillText = completed ? 'Completado' : failed ? 'Falló' : jobStatus === 'pending' ? 'En cola' : jobStatus === 'processing' ? 'Procesando' : jobStatus === 'loading' ? 'Cargando…' : 'Sin iniciar';

  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title ?? '—', href: '#/library/' + (a._id ?? '') }, { label: 'Analizar' }]} title="Analizar video" description={`${a.title ?? '—'} — convertir y analizar de forma automática.`} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Panel title="Trabajo de análisis" right={<Pill tone={pillTone}>{pillText}</Pill>}>
          {processing && (
            <div style={{ marginBottom: 16 }}>
              <Progress value={job?.progress ?? 0} />
              {job?.state && <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 6 }}>{ANALYSE_STAGE_LABEL[job.state] ?? job.state}</div>}
            </div>
          )}
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--body-muted)' }}>El análisis automático genera transcripción, escenas, fotogramas clave y enriquecimiento semántico para búsqueda.</p>
          <div style={{ display: 'flex', gap: 10 }}>
            <Btn variant="solid" icon="sparkles" loading={starting} disabled={!id || processing} onClick={() => { void startAnalysis({ assetId: id as any }); }}>{completed || failed ? 'Re-analizar' : 'Iniciar análisis'}</Btn>
          </div>
        </Panel>
        {failed && job?.error && (
          <Panel title="Detalle del fallo"><p style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.5 }}>{job.error}</p></Panel>
        )}
        {completed && (
          <>
            <div className="circa-grid-3">
              <StatCard label="Escenas detectadas" value={scenes ? String(scenes.length) : '—'} icon="film" />
              <StatCard label="Segmentos de audio" value={audio ? String(audio.length) : '—'} icon="activity" />
              <StatCard label="Fotogramas clave" value={keyframes ? String(keyframes.length) : '—'} icon="grid" />
            </div>
            <Panel title="Escenas y fotogramas clave">
              <SceneGallery assetId={id} />
            </Panel>
          </>
        )}
        {jobStatus === 'idle' && <Panel><EmptyState icon="sparkles" title="Aún no analizado" body="Inicia un análisis para generar transcripción, escenas y sugerencias de cortes." /></Panel>}
      </div>
    </div>
  );
}

export function EditorialInbox() {
  const { toast } = useToast();
  // T318 — honest empty state. The real editorial inbox surface lives at
  // `src/screens/editorial/Inbox.tsx` and binds to its own Convex query;
  // this legacy bundle entry renders an honest EmptyState until any
  // remaining routes pointing here are migrated.
  const [items, setItems] = useState<Array<{ _id?: string; id?: string; title?: string; source?: string; durationMs?: number; addedAt?: number }>>([]);
  return (
    <div>
      <PageHead title="Bandeja editorial" description="Revisa y acepta contenido de tus fuentes."
        actions={<Btn variant="ghost" icon="activity" onClick={() => navigate('/editorial-sync')}>Estado de sincronización</Btn>} />
      <Panel pad={16} style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 12 }}>
          <div style={{ width: 300 }}><Input icon="search" placeholder="Buscar" onChange={() => {}} value="" /></div>
          <Select value="Pendiente" onChange={() => {}} options={['Pendiente', 'Aceptado', 'Rechazado']} />
        </div>
      </Panel>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {items.length === 0 && <Panel><EmptyState icon="message" title="Bandeja vacía" body="No hay elementos pendientes de revisión." /></Panel>}
        {items.map(it => (
          <Panel key={it.id} pad={16}>
            <div style={{ display: 'flex', gap: 16 }}>
              <div style={{ width: 160, flexShrink: 0 }}><Thumb radius={12} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div onClick={() => navigate('/editorial-inbox/' + it.id)} style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500, marginBottom: 6, cursor: 'pointer' }}>{it.title}</div>
                <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--body-muted)', lineHeight: 1.45 }}>Contenido recibido desde una fuente conectada, listo para revisión editorial antes de ingresar a la biblioteca.</p>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Pill tone="info" size="sm">{it.source}</Pill><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{ago(it.addedAt)}</span><Pill tone="neutral" size="sm">{fmtDur(it.durationMs)}</Pill></div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'center' }}>
                <Btn size="sm" variant="ghost" icon="eye" onClick={() => navigate('/editorial-inbox/' + it.id)}>Revisar</Btn>
                <Btn size="sm" variant="solid" icon="check" onClick={() => { setItems(x => x.filter(y => y.id !== it.id)); toast({ variant: 'success', message: 'Aceptado y añadido a la biblioteca.' }); }}>Aceptar</Btn>
                <Btn size="sm" variant="text" onClick={() => { setItems(x => x.filter(y => y.id !== it.id)); toast({ variant: 'info', message: 'Elemento rechazado.' }); }}>Rechazar</Btn>
              </div>
            </div>
          </Panel>
        ))}
      </div>
    </div>
  );
}

/**
 * T328 (FR-005) — Real surface for `/feed-sources`.
 *
 * READ: `api.editorial.feeds.listAll` returns the projected rows of the
 * Payload-owned `ingest-sources` collection (synced through
 * `convex/sync/ingestSourcesReceiver.ts`). Honest-state primitives drive
 * the loading / empty branches — no fabricated mock rows.
 *
 * CREATE: chose approach (a) per task description — link-out to the
 * Payload admin create form. Rationale: ingestSources is one-way-synced;
 * the only legitimate write surface is Payload itself. Wrapping that
 * behind a Convex action proxy would (i) duplicate Payload's own
 * field-level validation, (ii) bypass Payload's admin audit log, and
 * (iii) re-introduce the dual-writer hazard the sync contract explicitly
 * forbids. The link-out keeps the integrity story simple and matches the
 * PRD §FR-005 acceptance criterion "a source created through the chosen
 * path carries a payloadId" (created in Payload → projected back through
 * the receiver → row arrives with its payloadId already populated).
 *
 * EDIT/VIEW per-row: same link-out into Payload's admin row page.
 *
 * VALIDATE per-row: calls `api.ingestSources.validate` (Convex action,
 * not a mutation) which probes the projected `sourceUrl`. The action
 * itself rejects non-https URLs with `E_INVALID_FEED_URL` — so even an
 * out-of-band Payload edit that violates the protocol guard surfaces as
 * a real failure, not a fake success toast.
 */
interface IngestSourceRow {
  _id?: string;
  payloadId?: string;
  name?: string;
  channelId?: string;
  protocol?: string;
  sourceUrl?: string;
  updatedAt?: number;
}

/** Build the Payload admin URL for an ingest-sources row. */
function payloadAdminUrl(payloadId?: string): string {
  const base = `${env.PAYLOAD_URL}/admin/collections/ingest-sources`;
  return payloadId ? `${base}/${payloadId}` : `${base}/create`;
}

export function FeedSources() {
  const { toast } = useToast();
  const sources = useQuery(api.editorial.feeds.listAll, {}) as
    | IngestSourceRow[]
    | undefined;
  const validate = useAction(api.ingestSources.validate);
  const [validating, setValidating] = useState<string | null>(null);

  async function onValidate(row: IngestSourceRow): Promise<void> {
    if (!row.payloadId) {
      toast({ variant: 'error', message: 'No se puede validar: falta el identificador de Payload.' });
      return;
    }
    setValidating(row.payloadId);
    try {
      const result = await validate({ payloadId: row.payloadId });
      if (result.ok) {
        toast({
          variant: 'success',
          message: `Fuente accesible (HTTP ${result.statusCode}).`,
        });
      } else if (result.reason === 'E_INVALID_FEED_URL') {
        toast({
          variant: 'error',
          message: 'La URL de la fuente debe usar https://. Edita la fuente en Payload para corregirla.',
        });
      } else if (result.reason === 'http_error') {
        toast({
          variant: 'error',
          message: `La fuente respondió HTTP ${result.statusCode}. Revisa la URL o las credenciales.`,
        });
      } else if (result.reason === 'network_error') {
        toast({
          variant: 'error',
          message: 'No se pudo contactar la fuente. Verifica conectividad y vuelve a intentarlo.',
        });
      } else {
        toast({
          variant: 'error',
          message: `Validación falló (${result.reason}).`,
        });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error desconocido';
      toast({ variant: 'error', message: `Validación falló: ${message}` });
    } finally {
      setValidating(null);
    }
  }

  function openInPayload(row?: IngestSourceRow): void {
    window.open(payloadAdminUrl(row?.payloadId), '_blank', 'noopener,noreferrer');
  }

  // Loading state: useQuery returns undefined until the first round-trip.
  if (sources === undefined) {
    return (
      <div>
        <PageHead title="Fuentes de contenido" description="Conecta feeds de socios — RSS, Atom o webhooks firmados — que Circa trae a tu bandeja para revisión."
          actions={<AccessGate perm="asset_create"><Btn variant="solid" icon="plus" onClick={() => openInPayload()}>Añadir fuente</Btn></AccessGate>} />
        <SkeletonList rows={3} />
      </div>
    );
  }

  // Honest-empty state: no fabricated rows.
  if (sources.length === 0) {
    return (
      <div>
        <PageHead title="Fuentes de contenido" description="Conecta feeds de socios — RSS, Atom o webhooks firmados — que Circa trae a tu bandeja para revisión."
          actions={<AccessGate perm="asset_create"><Btn variant="solid" icon="plus" onClick={() => openInPayload()}>Añadir fuente</Btn></AccessGate>} />
        <Panel>
          <EmptyState
            icon="database"
            title="Sin fuentes conectadas"
            body="Aún no hay fuentes de contenido configuradas. Añade una desde el panel de administración para empezar a recibir contenido."
            action={<AccessGate perm="asset_create"><Btn variant="solid" icon="plus" onClick={() => openInPayload()}>Añadir fuente</Btn></AccessGate>}
          />
        </Panel>
      </div>
    );
  }

  return (
    <div>
      <PageHead title="Fuentes de contenido" description="Conecta feeds de socios — RSS, Atom o webhooks firmados — que Circa trae a tu bandeja para revisión."
        actions={<AccessGate perm="asset_create"><Btn variant="solid" icon="plus" onClick={() => openInPayload()}>Añadir fuente</Btn></AccessGate>} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {sources.map((s: IngestSourceRow) => {
          const key = s._id ?? s.payloadId ?? s.name ?? Math.random().toString(36).slice(2);
          const displayName = s.name ?? 'Sin nombre';
          const displayUrl = s.sourceUrl ?? '—';
          const displayType = s.protocol ?? '—';
          const isValidating = validating === s.payloadId;
          return (
            <Panel key={key} pad={20}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <Icon name="database" size={22} color="var(--muted-slate)" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{displayName}</span>
                    <Pill tone="neutral" size="sm">{displayType}</Pill>
                  </div>
                  <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', textTransform: 'none' }}>{displayUrl}</div>
                </div>
                <Dropdown
                  trigger={<Btn variant="ghost" icon="moreH" />}
                  items={[
                    { label: 'Editar en el panel', icon: 'edit', onClick: () => openInPayload(s) },
                    { label: isValidating ? 'Validando…' : 'Validar ahora', icon: 'refresh', onClick: () => { void onValidate(s); } },
                  ]}
                />
              </div>
            </Panel>
          );
        })}
      </div>
    </div>
  );
}

/**
 * T332 (FR-010, WIRE-4) — `/search` left behind an honest
 * "próximamente" state.
 *
 * Rationale (per task description, [ASSUMED — no public search index]):
 * the backend ships a `search_title` full-text index
 * (`convex/schema.ts:942`) and an internal `assetQueries.searchAssetsByTitle`,
 * but there is no PUBLIC auth-gated wrapper for it in this milestone.
 * The prior implementation rendered a `CIRCA.assets.slice(0, 4)` mock —
 * a client-side substring filter dressed up as semantic search, which
 * silently fabricates "matches" for any query and is the CLAUDE.md
 * §12a "do not invent" anti-pattern.
 *
 * The honest replacement: a read-only "coming soon" panel that names
 * the missing capability (transcript / scene search) and points
 * operators at the working `/library` browse + title-filter affordance.
 * When a public `api.assets.search` query lands, this screen swaps
 * the panel for the live results list — no mock removal step needed.
 */
export function SmartSearch() {
  return (
    <div>
      <PageHead title="Búsqueda inteligente" description="Encuentra momentos en tus videos por lo que se dice o se ve." />
      <Panel>
        <EmptyState
          icon="sparkles"
          title="Búsqueda inteligente — próximamente"
          body="La búsqueda por transcripción y escena estará disponible cuando el análisis automático esté conectado. Mientras tanto, usa el filtro por título en la biblioteca."
          action={<Btn variant="solid" icon="film" onClick={() => navigate('/library')}>Ir a la biblioteca</Btn>}
        />
      </Panel>
    </div>
  );
}

export function AiSuggestions() {
  // T318 — wired to api.assetQueries.listAllAssets. The analysis-result
  // counts ("3 cambios de escena", "2 silencios", "2 alta confianza") are
  // NOT exposed by the public assets query, so they are honestly omitted
  // until a public scene/analysis index lands. Until then the list shows
  // recently-added videos with a "Revisar" affordance into the marker
  // editor — never fabricated analysis numbers.
  const aiAssets = useQuery(api.assetQueries.listAllAssets, {}) as
    | Array<{ _id: string; title?: string }>
    | undefined;
  return (
    <div>
      <PageHead title="Sugerencias inteligentes" description="Videos analizados recientemente." />
      {aiAssets === undefined ? (
        <SkeletonList rows={5} />
      ) : aiAssets.length === 0 ? (
        <EmptyState
          icon="sparkles"
          title="Sin sugerencias"
          body="Aún no hay videos analizados en este espacio de trabajo."
        />
      ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {aiAssets.slice(0, 5).map(a => (
          <Panel key={a._id} pad={16}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <div style={{ width: 130, flexShrink: 0 }}><Thumb radius={10} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontSize: 17, fontWeight: 500, marginBottom: 6 }}>{a.title ?? '—'}</div>
              </div>
              <Btn size="sm" variant="ghost" iconRight="arrowRight" onClick={() => navigate('/library/' + a._id + '/markers')}>Revisar</Btn>
            </div>
          </Panel>
        ))}
      </div>
      )}
    </div>
  );
}

